import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Shield,
  Plus,
  Trash2,
  RefreshCw,
  ShieldCheck,
  ShieldX
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlacklistPage = () => {
  const { getAuthHeader } = useAuth();
  const [blacklist, setBlacklist] = useState([]);
  const [whitelist, setWhitelist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState("blacklist");
  const [newIp, setNewIp] = useState("");
  const [newReason, setNewReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeader();
      const [blacklistRes, whitelistRes] = await Promise.all([
        axios.get(`${API}/blacklist`, { headers }),
        axios.get(`${API}/whitelist`, { headers })
      ]);
      
      setBlacklist(blacklistRes.data);
      setWhitelist(whitelistRes.data);
    } catch (error) {
      toast.error("Failed to fetch lists");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newIp || !newReason) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/${dialogType}`, 
        { ip_address: newIp, reason: newReason },
        { headers: getAuthHeader() }
      );
      toast.success(`IP added to ${dialogType}`);
      setDialogOpen(false);
      setNewIp("");
      setNewReason("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add IP");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (ip, type) => {
    try {
      await axios.delete(`${API}/${type}/${encodeURIComponent(ip)}`, {
        headers: getAuthHeader()
      });
      toast.success(`IP removed from ${type}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to remove IP");
    }
  };

  const openDialog = (type) => {
    setDialogType(type);
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="blacklist-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-mono font-bold tracking-tight flex items-center gap-3">
            <Shield className="w-7 h-7 text-primary" />
            IP Lists
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage blacklisted and whitelisted IPs
          </p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchData}
          className="gap-2"
          data-testid="refresh-lists-btn"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="blacklist" className="space-y-4">
        <TabsList className="bg-card border border-border">
          <TabsTrigger value="blacklist" className="gap-2 data-[state=active]:bg-red-500/10 data-[state=active]:text-red-500" data-testid="blacklist-tab">
            <ShieldX className="w-4 h-4" />
            Blacklist ({blacklist.length})
          </TabsTrigger>
          <TabsTrigger value="whitelist" className="gap-2 data-[state=active]:bg-emerald-500/10 data-[state=active]:text-emerald-500" data-testid="whitelist-tab">
            <ShieldCheck className="w-4 h-4" />
            Whitelist ({whitelist.length})
          </TabsTrigger>
        </TabsList>

        {/* Blacklist Tab */}
        <TabsContent value="blacklist">
          <Card className="bg-card/50 border-border" data-testid="blacklist-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <ShieldX className="w-4 h-4 text-red-500" />
                Blacklisted IPs
              </CardTitle>
              <Dialog open={dialogOpen && dialogType === "blacklist"} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="destructive" onClick={() => openDialog("blacklist")} data-testid="add-blacklist-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add IP
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-border">
                  <DialogHeader>
                    <DialogTitle>Add to Blacklist</DialogTitle>
                    <DialogDescription>
                      Add an IP address to the blacklist. This will flag all future requests from this IP as threats.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleAdd} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="ip">IP Address</Label>
                      <Input
                        id="ip"
                        placeholder="e.g., 192.168.1.1"
                        value={newIp}
                        onChange={(e) => setNewIp(e.target.value)}
                        className="bg-background font-mono"
                        data-testid="ip-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reason">Reason</Label>
                      <Input
                        id="reason"
                        placeholder="Reason for blacklisting"
                        value={newReason}
                        onChange={(e) => setNewReason(e.target.value)}
                        className="bg-background"
                        data-testid="reason-input"
                      />
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" variant="destructive" disabled={submitting} data-testid="confirm-add-btn">
                        Add to Blacklist
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-2">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="skeleton h-12 w-full" />
                  ))}
                </div>
              ) : blacklist.length > 0 ? (
                <div className="space-y-2">
                  {blacklist.map((entry) => (
                    <div 
                      key={entry.id} 
                      className="flex items-center justify-between p-3 bg-background rounded border border-border hover:border-red-500/30 transition-colors"
                    >
                      <div className="flex-1">
                        <p className="font-mono text-primary">{entry.ip_address}</p>
                        <p className="text-xs text-muted-foreground mt-1">{entry.reason}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground hidden sm:block">
                          {new Date(entry.created_at).toLocaleDateString()}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-red-500"
                          onClick={() => handleRemove(entry.ip_address, "blacklist")}
                          data-testid={`remove-blacklist-${entry.ip_address}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No blacklisted IPs
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Whitelist Tab */}
        <TabsContent value="whitelist">
          <Card className="bg-card/50 border-border" data-testid="whitelist-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-500" />
                Whitelisted IPs
              </CardTitle>
              <Dialog open={dialogOpen && dialogType === "whitelist"} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => openDialog("whitelist")} data-testid="add-whitelist-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add IP
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-border">
                  <DialogHeader>
                    <DialogTitle>Add to Whitelist</DialogTitle>
                    <DialogDescription>
                      Add an IP address to the whitelist. This will mark all requests from this IP as safe.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleAdd} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="ip-whitelist">IP Address</Label>
                      <Input
                        id="ip-whitelist"
                        placeholder="e.g., 192.168.1.1"
                        value={newIp}
                        onChange={(e) => setNewIp(e.target.value)}
                        className="bg-background font-mono"
                        data-testid="ip-whitelist-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reason-whitelist">Reason</Label>
                      <Input
                        id="reason-whitelist"
                        placeholder="Reason for whitelisting"
                        value={newReason}
                        onChange={(e) => setNewReason(e.target.value)}
                        className="bg-background"
                        data-testid="reason-whitelist-input"
                      />
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={submitting} data-testid="confirm-whitelist-btn">
                        Add to Whitelist
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-2">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="skeleton h-12 w-full" />
                  ))}
                </div>
              ) : whitelist.length > 0 ? (
                <div className="space-y-2">
                  {whitelist.map((entry) => (
                    <div 
                      key={entry.id} 
                      className="flex items-center justify-between p-3 bg-background rounded border border-border hover:border-emerald-500/30 transition-colors"
                    >
                      <div className="flex-1">
                        <p className="font-mono text-primary">{entry.ip_address}</p>
                        <p className="text-xs text-muted-foreground mt-1">{entry.reason}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground hidden sm:block">
                          {new Date(entry.created_at).toLocaleDateString()}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-red-500"
                          onClick={() => handleRemove(entry.ip_address, "whitelist")}
                          data-testid={`remove-whitelist-${entry.ip_address}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No whitelisted IPs
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BlacklistPage;
